import { Capacitor } from '@capacitor/core'
import { ScreenOrientation } from '@capacitor/screen-orientation'
import { StatusBar } from '@capacitor/status-bar'
import { Directory, Filesystem } from '@capacitor/filesystem'
import { Device } from '@capacitor/device'
import { App } from '@capacitor/app'
import { Toast } from '@capacitor/toast'
import { Keyboard } from '@capacitor/keyboard'
import { NativeBiometric } from 'capacitor-native-biometric'
import { detect } from 'detect-browser'

const System = {
  async lockScreenOrientation (orientation) {
    if (Capacitor.getPlatform() !== 'web' &&
      Capacitor.isPluginAvailable('ScreenOrientation')) {
      await ScreenOrientation.lock({ orientation })
    }
  },
  async unlockScreenOrientation () {
    if (Capacitor.getPlatform() !== 'web' &&
      Capacitor.isPluginAvailable('ScreenOrientation')) {
      await ScreenOrientation.unlock()
    }
  },
  async screenOrientation () {
    if (Capacitor.getPlatform() !== 'web' &&
      Capacitor.isPluginAvailable('ScreenOrientation')) {
      return ScreenOrientation.orientation()
    } else {
      return { type: null }
    }
  },
  async hideStatusBar () {
    if (Capacitor.isPluginAvailable('StatusBar')) {
      await StatusBar.hide()
    }
  },
  async setStatusBarStyle (style) {
    if (Capacitor.isPluginAvailable('StatusBar')) {
      await StatusBar.setStyle({ style })
    }
  },
  hasFilesystem () {
    return Capacitor.getPlatform() !== 'web' &&
      Capacitor.isPluginAvailable('Filesystem')
  },
  async deleteFile (path) {
    if (Capacitor.isPluginAvailable('Filesystem')) {
      await Filesystem.deleteFile({
        path,
        directory: Directory.Cache,
      })
    }
  },
  async fileExists (path, dir = '/') {
    if (Capacitor.isPluginAvailable('Filesystem')) {
      const { files } = await Filesystem.readdir({
        path: dir,
        directory: Directory.Cache,
      })
      
      return files.findIndex(file => file.name === path) !== -1
    } else {
      return false
    }
  },
  async dirExists (path) {
    if (Capacitor.isPluginAvailable('Filesystem')) {
      const { files } = await Filesystem.readdir({
        path: '/',
        directory: Directory.Cache,
      })
      
      return files.findIndex(file => file.name === path) !== -1
    } else {
      return false
    }
  },
  async readDir (path = '/') {
    if (Capacitor.isPluginAvailable('Filesystem')) {
      const { files } = await Filesystem.readdir({
        path,
        directory: Directory.Cache,
      })
      
      return files
    } else {
      return null
    }
  },
  async makeDir (path) {
    if (Capacitor.isPluginAvailable('Filesystem')) {
      await Filesystem.mkdir({
        path,
        directory: Directory.Cache,
        recursive: true,
      })
    }
  },
  async rename (from, to) {
    if (Capacitor.isPluginAvailable('Filesystem')) {
      await Filesystem.rename({
        from,
        to,
        directory: Directory.Cache,
      })
    }
  },
  async readFile (path) {
    if (Capacitor.isPluginAvailable('Filesystem')) {
      return Filesystem.readFile({
        path,
        directory: Directory.Cache,
      })
    } else {
      return { data: null }
    }
  },
  async writeFile ({
    path,
    data,
    recursive = true,
  }) {
    if (Capacitor.isPluginAvailable('Filesystem')) {
      return Filesystem.writeFile({
        path,
        data,
        directory: Directory.Cache,
        recursive,
      })
    } else {
      return { url: '' }
    }
  },
  async getUri (path) {
    if (Capacitor.isPluginAvailable('Filesystem')) {
      return Filesystem.getUri({
        path,
        directory: Directory.Cache,
      })
    } else {
      return { uri: '' }
    }
  },
  async keepAwake () {
    if (Capacitor.isPluginAvailable('KeepAwake') &&
      (await KeepAwake.isSupported()).isSupported) {
      await KeepAwake.keepAwake()
    }
  },
  async allowSleep () {
    if (Capacitor.isPluginAvailable('KeepAwake') &&
      (await KeepAwake.isSupported()).isSupported) {
      await KeepAwake.allowSleep()
    }
  },
  async onKeyboardChange (callback) {
    if (Capacitor.isPluginAvailable('Keyboard')) {
      await Keyboard.addListener('keyboardWillShow', () => {
        callback(true)
      })
      
      await Keyboard.addListener('keyboardWillHide', () => {
        callback(false)
      })
    }
  },
  async removeAllKeyboardListeners () {
    if (Capacitor.isPluginAvailable('Keyboard')) {
      await Keyboard.removeAllListeners()
    }
  },
  async showKeyboardAccessoryBar (show) {
    if (Capacitor.isPluginAvailable('Keyboard')) {
      try {
        await Keyboard.setAccessoryBarVisible({ isVisible: show })
      } catch (err) {
        console.error('Keyboard.setAccessoryBarVisible', err)
      }
      
    }
  },
  async isOnline () {
    if (Capacitor.isPluginAvailable('Network')) {
      return (await Network.getStatus())?.connected
    } else {
      return true
    }
  },
  async onNetworkChanged (callback) {
    if (Capacitor.isPluginAvailable('Network')) {
      const { remove } = await Network.addListener('networkStatusChange',
        callback)
      
      return remove
    } else {
      return function () {}
    }
  },
  async removeAllNetworkListeners () {
    if (Capacitor.isPluginAvailable('Network')) {
      await Network.removeAllListeners()
    }
  },
  async toast ({ text, duration = 'short', position = 'bottom' }) {
    if (Capacitor.isPluginAvailable('Toast')) {
      await Toast.show({ text, duration, position })
    } else {
      alert(text)
    }
  },
  async getDevicePlatformOption () {
    return (await System.getDeviceInfo()).platform.toLowerCase()
  },
  async getDeviceName () {
    const info = await System.getDeviceInfo()
    const browser = detect()
    
    return info.name ?? [browser.name, browser.version, '@', info.operatingSystem, info.osVersion].join(' ')
  },
  async getDeviceInfo () {
    return Device.getInfo()
  },
  async getDeviceId () {
    return Device.getId()
  },
  async getAppInfo () {
    if (Capacitor.getPlatform() === 'web') {
      return 'web'
    }
    
    return App.getInfo()
  },
  async biometryAvailable () {
    if (!Capacitor.isPluginAvailable('NativeBiometric')) {
      return false
    }
    
    return (await NativeBiometric.isAvailable()).isAvailable
  },
  async biometricVerify(options) {
    if (!Capacitor.isPluginAvailable('NativeBiometric')) {
      return false
    }
    
    return NativeBiometric.verifyIdentity(options)
      .then(() => true)
      .catch(() => false);
  }
}

export default System
